import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {ICar} from '../../../../models/Car'
import {ICarLocation} from '../../../../models/CarLocation'
import {BadgeType, prefectureToArea, ScreenType} from '../../../config/constants'
import {getCarUrl, localizeCar} from '../../../helpers/car'
import {localizeCarLocation} from '../../../helpers/carLocation'
import CarStars from '../../common/CarStars'
import LocaleText from '../../common/LocaleText'
import ResponsiveImg from '../../common/ResponsiveImg'
import TypeLabel from '../../common/TypeLabel'
import {useAmp, useLocale} from '../../hocs/hooks'
import NoImage from '../../../assets/imgs/no-image.png'
import styles from './CarPanel.scss'
import FaMapMarkerAlt from '../../../assets/icons/FaMapMarkerAlt.svg'
import { RouterContext } from '../../containers/Context'

const CarPanel = (
  {
    car,
    locations,
    area,
    prefecture,
    showExtendFee,
    showLabel = true,
    onSelect,
    isInfoWindow,
    viewReview = false,
    viewByList = false,
    orderView = false,
    checkIn,
    checkOut,
    firstSelection
  }: {
    car: ICar
    locations: ReadonlyArray<ICarLocation | null>
    area?: string // only show location in area, if null return first location
    prefecture?: string
    maximumFacilities?: number
    showExtendFee?: boolean
    showLabel?: boolean
    onSelect?: (car: ICar) => any
    isInfoWindow?: boolean
    viewReview?: boolean
    viewByList?: boolean
    orderView?: boolean
    checkIn?: string
    checkOut?: string
    firstSelection?: boolean
  }) => {
  const amp = useAmp()
  const locale = useLocale()
  const {
    photos,
    stars,
    totalReviews,
    capacity,
    sleepCapacity,
    category,
    name,
  } = useMemo(() => localizeCar(locale, car), [locale, car])
  let location
  if (locations?.length) {
    for (const curr of locations) {
      location = curr
      if (!location) break
      if (area && prefectureToArea[location.prefecture] === area) break
      if (prefecture && location.prefecture === prefecture) break
    }
  }
  location = location && localizeCarLocation(locale, location)
  const addressStation = location
    ? location?.nearestStation
      ? `${location?.basicAddress || ''}, ' ${location?.nearestStation || ''}`
      : location?.basicAddress
    : undefined
  const [{url = NoImage, caption} = {}] = photos
  const carUrlParams = checkIn && checkOut
    ? `?checkIn=${checkIn}&checkOut=${checkOut}`
    : ''
  const carUrl = `${getCarUrl(locale, car, false, false, amp)}${carUrlParams}`
  const {history} = useContext(RouterContext)
  const localOnSelect = useCallback(() => onSelect ? onSelect(car) : history.push(carUrl), [onSelect, car, history, carUrl])
  const isSuperHolder = !!car?.holder?.isSuperHolder
  return <a
      href={carUrl}
      onClick={() => {localOnSelect; return false}}
      className={[
        !isInfoWindow && viewByList && 'border rounded w-100',
        !isInfoWindow && viewReview && 'border rounded w-100',
        'bg-white overflow-hidden c-pointer',
        !viewReview && 'd-lg-flex',
        viewByList && 'h-100',
        isInfoWindow && 'align-items-center',
        viewByList ? styles.containerList : orderView ? styles.containerOrder : viewReview? styles.containerReviewList : styles.containerListMap,
        !viewByList && (firstSelection ? styles.cardMarginFirstLoad : styles.cardMargin)
      ].filter(Boolean).join(' ')}
    >
    <div className={[
      'position-relative',
      // 'p-lg-1 pl-1',
      viewByList && 'pl-lg-0',
      isInfoWindow && 'pt-1',
      !isInfoWindow && styles.imgMapMode
    ].filter(Boolean).join(' ')}>
      <div className={[
        'position-relative',
        !isInfoWindow && styles.spImage,
        isInfoWindow
          ? styles.imageMapsCardMapInfo
          : 
        viewReview
          ? styles.imageReviewCard 
          : styles.imageCardMapMode
      ].filter(Boolean).join(' ')}>
        {isSuperHolder && showLabel && <TypeLabel type={BadgeType.superholder}/>}
        <ResponsiveImg
          url={url}
          caption={caption}
          className={[
            !isInfoWindow && 'rounded-top',
            !isInfoWindow && styles.roundedMdImg,
          ].filter(Boolean).join(' ')}
        />
      </div>
    </div>
    <div className={[
      !isInfoWindow && 'pt-lg-2 pl-2 pl-13px pb-3 pb-lg-3 position-relative w-100',
      'd-flex flex-column pt-2',
      'pt-lg-1 position-lg-relative',
      isInfoWindow && 'pl-1',
      isInfoWindow
        ? styles.mapCardMapModeInfo
        :
      viewReview
        ? styles.textReviewCard
        : styles.textCardMapMode
    ].filter(Boolean).join(' ')}>
      <h2 className={[
        'h5 overflow-hidden font-weight-medium ellipsis d-block',
        styles.name,
        isInfoWindow ? 'mb-1' : 'pb-lg-2'
      ].filter(Boolean).join(' ')}>{name}</h2>
      <div
        className={['text-van ellipsis font-weight-medium', !isInfoWindow && 'mt-1 small'].filter(Boolean).join(' ')}
      >
        <FaMapMarkerAlt className="fa mb-1 mr-1 font-size-8"/>
        <span className={styles.address}>{addressStation || <FormattedMessage id="car.addressNotFound"/>}</span>
      </div>
      <div className={
          ['text-muted small ellipsis', isInfoWindow && 'mt-1', styles.capacity]
            .filter(Boolean).join(' ')
      }>
        <FormattedMessage
          id="car.summary"
          defaultMessage="Capacity:{capacity} people, Sleep capacity:{sleepCapacity} people | {category}"
          values={{
            capacity,
            sleepCapacity,
            category
          }}
        />
      </div>
      <CarStars
        stars={stars}
        total={totalReviews}
        className="small mt-1"
        starsStyle="font-size-12 font-weight-medium"
      />
      <div className={[
        'd-lg-flex justify-content-lg-between flex-shrink-lg-1 align-items-lg-start',
        isInfoWindow && styles.infoWindowPrice
      ].filter(Boolean).join(' ')}>
        <div className="flex-shrink-lg-0" />
        <div className={[
          'text-van font-weight-bold h6 mb-0 text-right w-100',
          viewByList && 'mr-2',
          (!viewByList && !isInfoWindow) ? '' : 'mb-md-2'
        ].filter(Boolean).join(' ')}>
          <div>
            <h3 className={
              ['d-inline-block mb-0 my-1 font-size-24 font-weight-bold', isInfoWindow && 'mt-2']
                .filter(Boolean).join(' ')
            }>
              ¥<FormattedNumber value={car.price || 0}/>〜
            </h3>/<FormattedMessage id="car.24h"/>
            <p className="text-right mb-0">
              ( <FormattedMessage id="station.oneNight" />
                <FormattedMessage
                  id="carStation.priceDetail"
                  values={{
                    price: <FormattedNumber value={car.price / sleepCapacity || 0}　minimumFractionDigits={0} maximumFractionDigits={0}/>
                  }}
                />
              /<LocaleText ja="人" en="person"/> )
            </p>
          </div>
          {showExtendFee && <div className="text-right">
              <p className="d-inline-block mr-2 font-weight-medium">
                (&nbsp;+<FormattedMessage
                  id="price.detail"
                  values={{
                    price: <FormattedNumber value={car.extendPrice || 0}/>
                  }}
                />
              </p>/<FormattedMessage id="car.12h" defaultMessage="12 hours"/>&nbsp;)
            </div>
          }
        </div>
      </div>
    </div>
  </a>
}
CarPanel.displayName = 'CarPanel'
export default CarPanel
